import React, { useContext, useState } from 'react';

import { compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import { ActiveIntegrationContext } from 'common/containers/ActiveIntegrationsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import connect from 'common/core/connect';
import ErrorMessage from 'common/ErrorMessage';
import Helmet from 'common/helmets/Helmet';
import useMutation from 'common/hooks/useMutation';
import withAccessControl from 'common/routing/withAccessControl';
import AdminFeatureUpsell from 'common/subdomain/admin/AdminFeatureUpsell';
import AdminIntegrationLimitUpsell from 'common/subdomain/admin/AdminIntegrationLimitUpsell';
import AdminSettingsHeader from 'common/subdomain/admin/AdminSettings/AdminSettingsHeader';
import Alert from 'common/ui/Alert';
import VStack from 'common/ui/VStack';
import AlertDialog from 'common/uiV3/AlertDialog';
import ButtonV3 from 'common/uiV3/Button/Button';
import ButtonLink from 'common/uiV3/Button/ButtonLink';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';

import type { Company } from 'common/api/endpoints/companies';
import type { SuccessResponse } from 'common/api/responses';

type OwnProps = Record<string, never>;

type ConnectProps = {
  reloadCompany: () => Promise<void>;
};

type Props = OwnProps & ConnectProps;

const AdminOpenAISettings = ({ reloadCompany }: Props) => {
  const company = useContext<Company>(CompanyContext);
  const { integrationCount, integrationLimit } = useContext(ActiveIntegrationContext);
  const [isUninstallDialogOpen, setIsUninstallDialogOpen] = useState(false);

  const hasMCPFeature = !!company.features.mcp;
  const hasOpenAIConnector = !!company.activeIntegrations?.openai;
  const atIntegrationLimit = !!integrationLimit && integrationCount >= integrationLimit;
  const isIDBEnabled = company.featureAllowlist?.includes('idea-database');

  const uninstallMutation = useMutation<SuccessResponse>({
    mutationFn: async () =>
      AJAX.post('/api/mcp/uninstall', {
        client: 'openai',
      }),
    onSuccess: async () => {
      await reloadCompany();
      setIsUninstallDialogOpen(false);
    },
    onError: () => {
      setIsUninstallDialogOpen(false);
    },
  });

  const renderContent = () => {
    if (!hasMCPFeature && !hasOpenAIConnector) {
      return (
        <VStack className="gap-4">
          <AdminFeatureUpsell feature="openai" cta="Connect Canny to OpenAI" />
        </VStack>
      );
    }

    if (!hasOpenAIConnector && atIntegrationLimit) {
      return <AdminIntegrationLimitUpsell />;
    }

    return (
      <VStack className="gap-4">
        <p>
          Use the OpenAI connector to give ChatGPT and other OpenAI tools safe, scoped access to
          your Canny workspace. The connector lets models list segments, ideas, and more using
          Canny's MCP tools. <strong>This integration is in beta.</strong>
        </p>
        {hasMCPFeature ? (
          <>
            <p>
              To connect with OpenAI, install the connector from the OpenAI interface and complete
              the authorization flow as described in the{' '}
              <ButtonLink
                variant="link"
                href="https://help.canny.io/en/articles/13063190-mcp-server-beta"
                target="_blank"
                rel="noopener">
                OpenAI connector setup instructions
              </ButtonLink>
              .
            </p>
            {!isIDBEnabled && !hasOpenAIConnector ? (
              <Alert type={Alert.AlertTypes.Info}>
                <Alert.Heading>Enable Canny Ideas</Alert.Heading>
                <Alert.Content>
                  To connect your canny workspace to OpenAI, you need to enable Canny Ideas on your
                  workspace. Follow the instructions in the{' '}
                  <ButtonLink
                    variant="link"
                    href="https://help.canny.io/en/articles/12903944-canny-ideas-onboarding"
                    target="_blank"
                    rel="noopener">
                    Canny Ideas onboarding guide
                  </ButtonLink>{' '}
                  to start using it.
                </Alert.Content>
              </Alert>
            ) : null}
          </>
        ) : (
          <p className="text-red-600">
            The OpenAI connector is not included in your current plan. The OpenAI connector will not
            be usable until you upgrade, but you can still uninstall it below.
          </p>
        )}
        {hasOpenAIConnector ? (
          <Alert type={Alert.AlertTypes.Confirmation}>
            <Alert.Heading>OpenAI connector installed</Alert.Heading>
            <Alert.Content>Your have connected your Canny worspace with OpenAI.</Alert.Content>
            <Alert.CTA>
              <AlertDialog open={isUninstallDialogOpen} onOpenChange={setIsUninstallDialogOpen}>
                <AlertDialog.Trigger asChild>
                  <ButtonV3
                    variant="outlined"
                    color="error"
                    disabled={uninstallMutation.isLoading}
                    isLoading={uninstallMutation.isLoading}
                    onClick={uninstallMutation.mutate}>
                    Uninstall connector
                  </ButtonV3>
                </AlertDialog.Trigger>
                <AlertDialog.Content>
                  <AlertDialog.Header>
                    <AlertDialog.Title>Uninstall OpenAI connector?</AlertDialog.Title>
                    <AlertDialog.Description>
                      Uninstalling the OpenAI connector will disconnect OpenAI for all admins in
                      your Canny workspace. You can reconnect it later from the
                      OpenAI&nbsp;interface.
                    </AlertDialog.Description>
                  </AlertDialog.Header>
                  <AlertDialog.Footer>
                    <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                    <ButtonV3
                      color="error"
                      onClick={uninstallMutation.mutate}
                      isLoading={uninstallMutation.isLoading}>
                      Uninstall connector
                    </ButtonV3>
                  </AlertDialog.Footer>
                </AlertDialog.Content>
              </AlertDialog>
            </Alert.CTA>
          </Alert>
        ) : null}
      </VStack>
    );
  };

  return (
    <div className="adminOpenAISettings">
      <Helmet title="OpenAI Integration | Canny" />
      <AdminSettingsHeader
        title="OpenAI Integration"
        subheading="Analyze feedback, streamline workflows, and unlock smarter insights by connecting with OpenAI."
      />
      <VStack className="content px-8 py-4">
        <VStack className="max-w-[780px] gap-4">
          {renderContent()}
          {uninstallMutation.error && (
            <ErrorMessage>{uninstallMutation.error.message}</ErrorMessage>
          )}
        </VStack>
      </VStack>
    </div>
  );
};

export default compose(
  connect(null, { reloadCompany }),
  withAccessControl<Props>(
    testEveryPermission(RoutePermissions.integrations.openai),
    '/admin/settings',
  ),
)(AdminOpenAISettings) as unknown as React.FC<OwnProps>;
